<app-form-dialog-shell
  [icon]="isEditing ? 'edit' : 'add_circle'"
  [heading]="isEditing ? 'Editar rol' : 'Nuevo rol'"
  [saving]="saving()"
  [saveLabel]="isEditing ? 'Guardar cambios' : 'Crear rol'"
  [saveIcon]="isEditing ? 'save' : 'add'"
  formId="role-form"
  (cancelled)="close()"
  >
<form id="role-form" [formGroup]="form" (ngSubmit)="onSubmit()" class="role-form">

      <!-- ── Info básica ──────────────────────────────────────── -->
      <div class="section-label">
        <mat-icon>info</mat-icon>
        <span>Información general</span>
      </div>

      <div class="basic-grid">
        <mat-form-field appearance="outline">
          <mat-label>Nombre del rol</mat-label>
          <input matInput formControlName="name" placeholder="ej. Supervisor" />
          @if (fieldError('name')) {<mat-error>Nombre requerido (mín. 2 caracteres)</mat-error>}
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>Descripción</mat-label>
          <input matInput formControlName="description" />
        </mat-form-field>
      </div>

      <mat-divider></mat-divider>

      <!-- ── Permisos por módulo ─────────────────────────────────
      Cada acordeón corresponde a un módulo del sistema.
      El checkbox del header decide si el módulo está incluido
      en el rol (navAccessTree). El cuerpo solo se expande si
      el módulo está marcado.
      ─────────────────────────────────────────────────────────── -->
      <div class="section-label">
        <mat-icon>schema</mat-icon>
        <span>Módulos y permisos asignados</span>
      </div>

      @if (availableModules.length === 0) {
        <p class="empty-hint">No hay módulos activos disponibles. Crea módulos primero.</p>
      }

      @if (availableModules.length > 0) {
        <!-- Buscador rápido: escribe para encontrar el módulo; Enter activa el primero -->
        <div class="modules-search">
          <mat-form-field appearance="outline" subscriptSizing="dynamic" class="modules-search__field">
            <mat-icon matPrefix>search</mat-icon>
            <mat-label>Buscar módulo</mat-label>
            <input matInput [value]="moduleQuery()" (input)="moduleQuery.set($any($event.target).value)"
                   (keydown.enter)="$event.preventDefault(); activateFirstMatch()" placeholder="Nombre, etiqueta o ruta… (Enter activa el primero)" autocomplete="off">
            @if (moduleQuery()) {
              <button matSuffix mat-icon-button type="button" aria-label="Limpiar" (click)="moduleQuery.set('')"><mat-icon>close</mat-icon></button>
            }
          </mat-form-field>
          <mat-button-toggle-group class="modules-search__scope" [value]="moduleScope()" (change)="moduleScope.set($event.value)" [hideSingleSelectionIndicator]="true" aria-label="Mostrar">
            <mat-button-toggle value="all">Todos</mat-button-toggle>
            <mat-button-toggle value="on">Asignados <b>{{ selectedModules().length }}</b></mat-button-toggle>
            <mat-button-toggle value="off">Sin asignar</mat-button-toggle>
          </mat-button-toggle-group>
        </div>
        <div class="modules-search__meta">
          {{ filteredModules().length }} de {{ availableModules.length }} módulos
          @if (moduleQuery() || moduleScope() !== 'all') {
            · <button type="button" class="link-btn" (click)="moduleQuery.set(''); moduleScope.set('all')">Ver todos</button>
          }
        </div>
        @if (filteredModules().length === 0) {
          <p class="empty-hint">Ningún módulo coincide con la búsqueda.</p>
        }
      }

      <mat-accordion class="modules-accordion" multi>
        @for (mod of filteredModules(); track mod.id) {
          <mat-expansion-panel
            [expanded]="isModuleSelected(mod.id!)"
            [disabled]="!isModuleSelected(mod.id!)"
            class="module-panel"
            [class.module-active]="isModuleSelected(mod.id!)"
            >
            <mat-expansion-panel-header>
              <mat-panel-title>
                <!-- Checkbox del header: incluir/excluir módulo del rol -->
                <mat-checkbox
                  color="primary"
                  [checked]="isModuleSelected(mod.id!)"
                  (change)="toggleModule(mod.id!, $event.checked)"
                  (click)="$event.stopPropagation()"
                  class="module-check"
                  >
                </mat-checkbox>
                <mat-icon class="mod-icon">{{ mod.icon || 'widgets' }}</mat-icon>
                <span class="mod-name">{{ mod.label }}</span>
              </mat-panel-title>

              <mat-panel-description>
                @if (isModuleSelected(mod.id!)) {
                  @let count = permissionsCountFor(mod.id!);
                  @if (count > 0) {
                    <span class="perm-count">
                      {{ count }} {{ count === 1 ? 'permiso asignado' : 'permisos asignados' }}
                    </span>
                  } @else {
                    <span class="perm-count empty">Sin permisos asignados</span>
                  }
                } @else {
                  <span class="perm-count disabled">Módulo no incluido</span>
                }
              </mat-panel-description>
            </mat-expansion-panel-header>

            <!-- Contenido del acordeón: matriz de permisos -->
            <div class="permissions-matrix">

              <!-- Atajos -->
              <div class="matrix-actions">
                <button
                  mat-stroked-button
                  type="button"
                  color="primary"
                  (click)="selectAllPermissionsFor(mod.id!)"
                  [disabled]="availablePermissions.length === 0"
                  >
                  <mat-icon>done_all</mat-icon>
                  Marcar todos
                </button>
                <button
                  mat-stroked-button
                  type="button"
                  (click)="clearPermissionsFor(mod.id!)"
                  [disabled]="permissionsCountFor(mod.id!) === 0"
                  >
                  <mat-icon>clear</mat-icon>
                  Limpiar
                </button>
              </div>

              <!-- Lista de permisos -->
              @if (availablePermissions.length === 0) {
                <p class="empty-hint">No hay permisos activos disponibles. Crea permisos primero.</p>
              } @else {
                <div class="permissions-grid">
                  @for (perm of availablePermissions; track perm.id) {
                    <label class="perm-item">
                      <mat-checkbox
                        color="primary"
                        [checked]="isPermissionSelected(mod.id!, perm.id!)"
                        (change)="togglePermission(mod.id!, perm.id!, $event.checked)"
                        >
                      </mat-checkbox>
                      <div class="perm-info">
                        <span class="perm-name">{{ perm.name }}</span>
                        @if (perm.description) {
                          <span class="perm-desc">{{ perm.description }}</span>
                        }
                      </div>
                    </label>
                  }
                </div>
              }

            </div>
          </mat-expansion-panel>
        }
      </mat-accordion>

      

    </form>
</app-form-dialog-shell>
